import { PlusIcon } from '@langchain/untitled-ui-icons';
import { Button, LinearProgress } from '@mui/joy';

import { useState } from 'react';
import { useDebounce } from 'use-debounce';

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/Command';
import { ShowAllPickerExtraItem } from '@/components/ResourceTagFilter/ShowAllPickerExtraItem';
import { useStoredResourceTags } from '@/hooks/useStoredResourceTags/useStoredResourceTags';
import { useDatasets } from '@/hooks/useSwr';
import { DatasetSchema, RunSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { evaluateIfRunCanBeAddedToDataset } from '../../../utils/compare-schemas';
import { RunsTablePopoverItem } from './RunsTablePopoverItem';

export const DatasetSelectorPopoverContent = (props: {
  runTypeAndInputs?: Pick<RunSchema, 'run_type' | 'inputs'>;
  handleSelectDataset: (dataset: DatasetSchema) => void;
  openNewDatasetPane: () => void;
  className?: string;
}) => {
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 250);

  const [showAll, setShowAll] = useState(false);
  const { selectedTagIds } = useStoredResourceTags();
  const { data: datasets } = useDatasets({
    name_contains: debouncedSearch,
    tag_value_id: showAll ? undefined : selectedTagIds,
    exclude_corrections_datasets: true,
  });

  let compatibleDatasets: DatasetSchema[] = [];
  let numIncompatibleDatasets = 0;

  if (datasets) {
    let incompatibleDatasetsCount = 0;
    compatibleDatasets = datasets.filter((dataset) => {
      if (!props.runTypeAndInputs) {
        return true;
      }
      if (
        evaluateIfRunCanBeAddedToDataset(
          props.runTypeAndInputs,
          dataset.data_type
        )
      ) {
        return true;
      } else {
        incompatibleDatasetsCount++;
        return false;
      }
    });
    compatibleDatasets = compatibleDatasets.sort((a, b) => {
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
    numIncompatibleDatasets = incompatibleDatasetsCount;
  }

  return (
    <Command
      shouldFilter={false}
      className={cn('h-[500px] overflow-hidden', props.className)}
    >
      <div className="flex h-full max-h-full flex-col overflow-hidden">
        <div className="inline-flex w-full items-center justify-between py-3 pr-3">
          <div className="px-4 text-sm font-semibold leading-normal">
            Add to Dataset
          </div>
          <Button
            variant="outlined"
            color="neutral"
            size="sm"
            startDecorator={<PlusIcon className="size-4" />}
            onClick={props.openNewDatasetPane}
          >
            New Dataset
          </Button>
        </div>
        <div className="px-3">
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search Datasets..."
            className="text-sm outline-none"
          />
        </div>
        <div className="mt-3 flex-grow overflow-y-auto overflow-x-hidden">
          <CommandList className="max-h-full whitespace-normal">
            {datasets ? (
              <CommandGroup
                className="m-0 [&_[cmdk-group-heading]]:bg-secondary [&_[cmdk-group-heading]]:px-5 [&_[cmdk-group-items]]:py-1.5"
                heading={
                  <span className="text-xs font-medium uppercase tracking-wide text-tertiary">
                    Compatible datasets
                  </span>
                }
              >
                <>
                  {compatibleDatasets.map((dataset, index) => {
                    return (
                      <CommandItem
                        key={index}
                        className="mx-1.5 flex cursor-pointer items-center px-0 py-3"
                        onSelect={() => {
                          props.handleSelectDataset(dataset);
                        }}
                      >
                        <RunsTablePopoverItem
                          title={dataset.name}
                          subtitle={dataset.description}
                        />
                      </CommandItem>
                    );
                  })}
                  <div className="mx-1.5 flex h-10 items-center py-3">
                    <RunsTablePopoverItem
                      subtitle={
                        <ShowAllPickerExtraItem
                          showAll={showAll}
                          resources={compatibleDatasets}
                          setShowAll={setShowAll}
                          hasSelectedTags={selectedTagIds.length > 0}
                        />
                      }
                    />
                  </div>
                </>
              </CommandGroup>
            ) : (
              <LinearProgress />
            )}
            {datasets && numIncompatibleDatasets > 0 && (
              <CommandGroup
                className="m-0 [&_[cmdk-group-heading]]:bg-secondary [&_[cmdk-group-heading]]:px-5 [&_[cmdk-group-items]]:py-1.5"
                heading={
                  <span className="text-xs font-medium uppercase tracking-wide text-tertiary">
                    Incompatible datasets
                  </span>
                }
              >
                <div className="px-1.5 py-2">
                  <RunsTablePopoverItem
                    subtitle={`${numIncompatibleDatasets} ${
                      numIncompatibleDatasets > 1 ? 'datasets' : 'dataset'
                    }`}
                  />
                </div>
              </CommandGroup>
            )}
          </CommandList>
        </div>
      </div>
    </Command>
  );
};
